'use client';

import type { ServicesSectionProps as CoreServicesSectionProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';
import {
    ArrowUpRight,
    Building2,
    Compass,
    Crown,
    FileCheck,
    Globe,
    Home,
    Key,
    Landmark,
    LineChart,
    Plane,
    Scale,
    Sparkles
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

// Extended props with Stone Investment-specific layouts
interface ServicesSectionProps extends Omit<CoreServicesSectionProps, 'layout'> {
  layout?: 'grid' | 'list' | 'numbered-list' | 'bento' | 'bento-grid' | 'vista' | 'vista-cards' | 'ledger';
}

// Extended service type with image support
interface ServiceWithImage {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  icon?: string;
  image?: string;
  link?: string;
  linkText?: string;
}

// Icon mapping for services
const iconMap: Record<string, React.ReactNode> = {
  search: <Compass size={24} strokeWidth={1} />,
  acquisition: <Key size={24} strokeWidth={1} />,
  sales: <LineChart size={24} strokeWidth={1} />,
  prestige: <Crown size={24} strokeWidth={1} />,
  valuations: <FileCheck size={24} strokeWidth={1} />,
  mauritius: <Globe size={24} strokeWidth={1} />,
  architecture: <Landmark size={24} strokeWidth={1} />,
  consulting: <Sparkles size={24} strokeWidth={1} />,
  management: <Home size={24} strokeWidth={1} />,
  immigration: <Plane size={24} strokeWidth={1} />,
  incorporation: <Building2 size={24} strokeWidth={1} />,
  legal: <Scale size={24} strokeWidth={1} />,
  concierge: <Key size={24} strokeWidth={1} />,
};

// Get icon component by id or name
const getServiceIcon = (service: ServiceWithImage, size: number = 24) => {
  const iconId = service.icon || service.id;
  const IconComponent = iconMap[iconId];
  if (IconComponent) return IconComponent;
  
  // Default icon
  return <Sparkles size={size} strokeWidth={1} />;
};

/**
 * Stone Investment Services Section
 * 
 * Concept One Design - Multiple Layout Options:
 * - 'ledger': Minimalist accordion list with hover-expand (Sample UI ServiceLedger)
 * - 'bento-grid': 2x2 corporate grid with color inversion (Sample UI ServiceBento)
 * - 'vista-cards': Visual cards with full background images (Sample UI ServiceVista)
 * - 'bento': Asymmetric grid with featured large card and smaller cards
 * - 'numbered-list': Editorial numbered list (original style)
 * - 'vista': Full-width cards with large typography
 */
// Extended props with messages support
interface ExtendedServicesSectionProps extends ServicesSectionProps {
  messages?: Record<string, unknown>;
}

export function StoneInvestmentServicesSection({ 
  title: titleProp, 
  subtitle, 
  services: servicesProp, 
  layout = 'bento',
  messages
}: ExtendedServicesSectionProps) {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const servicesMessages = (stoneMessages.services || {}) as Record<string, string>;
  const sectionMessages = (stoneMessages.servicesSection || {}) as Record<string, unknown>;
  const translatedServices = sectionMessages.services as Array<{ id: string; title: string; description: string }> | undefined;
  
  const t = (key: string, fallback: string): string => {
    return (sectionMessages[key] as string) || servicesMessages[key] || fallback;
  };

  // Merge translated service items with prop-based ones (preserving links)
  const services = servicesProp.map((svc, idx) => {
    const translated = translatedServices?.find(ts => ts.id === svc.id) || translatedServices?.[idx];
    if (translated) {
      return { ...svc, title: translated.title, description: translated.description };
    }
    return svc;
  });

  const title = t('eyebrow', titleProp || 'Our Expertise');
  
  // ==========================================================================
  // LEDGER LAYOUT - Concept One Sample UI "ServiceLedger" (EXACT MATCH)
  // Minimalist accordion list with hover-expand interactions
  // ==========================================================================
  if (layout === 'ledger') {
    return (
      <section className="py-24 md:py-40 px-6 md:px-12 bg-stone-50" id="services">
        <div className="container mx-auto">
          {/* Header */}
          {(title || subtitle) && (
            <div className="text-center mb-16">
              {subtitle && (
                <span 
                  className="text-stone-400 uppercase tracking-[0.3em] text-xs block mb-4"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {subtitle}
                </span>
              )}
              {title && (
                <h2 
                  className="text-4xl md:text-5xl text-stone-900"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {title}
                </h2>
              )}
            </div>
          )}

          {/* Ledger List */}
          <div className="w-full max-w-4xl mx-auto bg-stone-50 border-t border-b border-stone-200">
            {services.map((service, idx) => (
              <div 
                key={service.id}
                className={`group relative border-b border-stone-200 last:border-b-0 cursor-pointer transition-all duration-500 overflow-hidden ${
                  activeIndex === idx ? 'bg-stone-100 py-12' : 'bg-transparent py-8 hover:bg-stone-50/50'
                }`}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                <div className="flex items-baseline px-8">
                  <span 
                    className={`font-sans text-xs uppercase tracking-widest w-12 transition-colors ${
                      activeIndex === idx ? 'text-stone-900 font-bold' : 'text-stone-400'
                    }`}
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    0{idx + 1}
                  </span>
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <h3 
                        className={`text-2xl md:text-3xl transition-all duration-500 ${
                          activeIndex === idx ? 'text-stone-900 translate-x-4 italic' : 'text-stone-500'
                        }`}
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                      >
                        {service.title}
                      </h3>
                      <div className={`transition-all duration-500 ${
                        activeIndex === idx ? 'opacity-100 rotate-45' : 'opacity-0 rotate-0'
                      }`}>
                        <ArrowUpRight size={24} className="text-stone-900" />
                      </div>
                    </div>
                    
                    <div className={`grid grid-cols-12 gap-8 mt-6 transition-all duration-700 ease-in-out ${
                      activeIndex === idx ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="col-span-12 md:col-span-8">
                        <p 
                          className="font-light text-stone-600 leading-relaxed max-w-lg"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {service.description}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-4 flex items-end justify-end">
                        {service.link ? (
                          <Link 
                            href={service.link}
                            className="text-[10px] uppercase tracking-widest text-stone-400 border border-stone-300 px-3 py-1 rounded-full hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                          >
                            {service.linkText || t('learnMore', 'Learn More')}
                          </Link>
                        ) : (
                          <span 
                            className="text-[10px] uppercase tracking-widest text-stone-400 border border-stone-300 px-3 py-1 rounded-full"
                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                          >
                            {t('learnMore', 'Learn More')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ==========================================================================
  // BENTO-GRID LAYOUT - Concept One Sample UI "ServiceBento" (EXACT MATCH)
  // 2x2 corporate grid with color inversion on hover
  // ==========================================================================
  if (layout === 'bento-grid') {
    return (
      <section className="py-24 md:py-40 px-6 md:px-12 bg-stone-50" id="services">
        <div className="container mx-auto">
          {/* Header */}
          {(title || subtitle) && (
            <div className="text-center mb-16">
              {subtitle && (
                <span 
                  className="text-stone-400 uppercase tracking-[0.3em] text-xs block mb-4"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {subtitle}
                </span>
              )}
              {title && (
                <h2 
                  className="text-4xl md:text-5xl text-stone-900"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {title}
                </h2>
              )}
            </div>
          )}

          {/* Bento Grid */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
            {services.map((service, idx) => (
              <motion.div 
                key={service.id}
                className="group relative bg-stone-50 h-[320px] p-12 flex flex-col justify-between overflow-hidden hover:bg-stone-900 transition-colors duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                {/* Icon Header */}
                <div className="relative z-10 flex justify-between items-start">
                  <div className="p-3 bg-stone-100 text-stone-900 group-hover:bg-stone-800 group-hover:text-white transition-colors duration-500 rounded-sm">
                    {getServiceIcon(service as ServiceWithImage)}
                  </div>
                  <span 
                    className="text-[10px] uppercase tracking-widest text-stone-400 group-hover:text-stone-500 transition-colors"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t('service', 'Service')} 0{idx + 1}
                  </span>
                </div>

                {/* Content */}
                <div className="relative z-10 space-y-4">
                  <h3 
                    className="text-2xl md:text-3xl text-stone-900 group-hover:text-white transition-colors duration-500"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {service.title}
                  </h3>
                  <p 
                    className="font-light text-sm text-stone-500 group-hover:text-stone-400 leading-relaxed transition-all duration-500 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {service.description}
                  </p>
                </div>

                {/* Background Pattern on Hover */}
                <div className="absolute top-0 right-0 p-12 opacity-0 group-hover:opacity-5 transition-opacity duration-700 pointer-events-none">
                  <div className="text-white transform rotate-12 scale-[8]">
                    {getServiceIcon(service as ServiceWithImage, 24)}
                  </div>
                </div>
                
                {/* Corner Accent */}
                <div className="absolute bottom-0 right-0 w-0 h-0 border-t-40 border-t-transparent border-r-40 border-r-stone-800 transition-all duration-500 opacity-0 group-hover:opacity-100" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ==========================================================================
  // VISTA-CARDS LAYOUT - Concept One Sample UI "ServiceVista" (EXACT MATCH)
  // Visual cards with full background images, 4-column grid
  // ==========================================================================
  if (layout === 'vista-cards') {
    return (
      <section className="py-24 md:py-40 px-6 md:px-12 bg-stone-50" id="services">
        <div className="container mx-auto">
          {/* Header */}
          {(title || subtitle) && (
            <div className="text-center mb-16">
              {subtitle && (
                <span 
                  className="text-stone-400 uppercase tracking-[0.3em] text-xs block mb-4"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {subtitle}
                </span>
              )}
              {title && (
                <h2 
                  className="text-4xl md:text-5xl text-stone-900 italic"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {title}
                </h2>
              )}
            </div>
          )}

          {/* Vista Cards Grid */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const serviceWithImage = service as ServiceWithImage;
              const defaultImage = `https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2670&auto=format&fit=crop`;
              
              return (
                <motion.div 
                  key={service.id}
                  className="group relative w-full aspect-2/3 md:aspect-9/16 bg-stone-200 overflow-hidden cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  {/* Background Image */}
                  <Image 
                    src={serviceWithImage.image || defaultImage}
                    alt={service.title}
                    fill
                    className="object-cover transition-transform duration-2000 group-hover:scale-110 grayscale group-hover:grayscale-0"
                  />
                  
                  {/* Dark Gradient Overlay */}
                  <div className="absolute inset-0 bg-linear-to-b from-stone-900/10 via-stone-900/20 to-stone-900/90 group-hover:opacity-90 transition-opacity duration-500" />

                  {/* Content - Positioned Bottom */}
                  <div className="absolute bottom-0 left-0 w-full p-8 text-white transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                    {/* Animated Line */}
                    <div className="w-0 group-hover:w-12 h-px bg-white mb-6 transition-all duration-700 ease-out" />
                    
                    <div className="mb-4 text-white/70 group-hover:text-white transition-colors">
                      {getServiceIcon(serviceWithImage)}
                    </div>
                    
                    <h3 
                      className="text-2xl md:text-3xl mb-2 italic"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      {service.title.split(' ')[0]} <br/>
                      <span className="not-italic">{service.title.split(' ').slice(1).join(' ')}</span>
                    </h3>
                    
                    <p 
                      className="text-xs font-light text-white/70 leading-relaxed max-h-0 group-hover:max-h-24 overflow-hidden transition-all duration-700"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {service.description}
                    </p>
                  </div>

                  {/* Number Top Right */}
                  <div 
                    className="absolute top-6 right-6 text-[10px] text-white/50 uppercase tracking-widest border border-white/20 px-2 py-1"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    0{idx + 1}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }
  
  // BENTO LAYOUT - Concept One flagship layout
  if (layout === 'bento') {
    const [featured, ...rest] = services;
    
    return (
      <section className="py-24 md:py-40 px-6 md:px-12 bg-stone-50" id="services">
        <div className="container mx-auto">
          <div className="flex flex-col items-center mb-20">
            <span 
              className="text-stone-700 uppercase tracking-[0.3em] text-sm mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('ourExpertise', 'Our Expertise')}
            </span>
            <h2 
              className="text-4xl md:text-6xl text-stone-900 text-center max-w-3xl"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title || t('defaultTitle', 'Bespoke Real Estate Services')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Featured Card - Spans 2 cols on lg */}
            {featured && (
              <motion.div 
                className="lg:col-span-2 group relative bg-stone-900 text-stone-50 p-8 md:p-12 min-h-[400px] flex flex-col justify-between overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Decorative Element */}
                <div className="absolute top-8 right-8 text-stone-800">
                  <div className="w-32 h-32 border border-stone-700 rounded-full flex items-center justify-center">
                    {iconMap[featured.id] || iconMap.consulting}
                  </div>
                </div>
                
                <div>
                  <span 
                    className="text-xs uppercase tracking-widest text-stone-500 mb-4 block"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    01
                  </span>
                  <h3 
                    className="text-3xl md:text-4xl mb-6 max-w-md"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {featured.title}
                  </h3>
                </div>
                
                <div>
                  <p 
                    className="text-stone-400 font-light max-w-md mb-8"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {featured.description}
                  </p>
                  {featured.link && (
                    <Link 
                      href={featured.link}
                      className="inline-flex items-center gap-2 text-stone-50 hover:text-stone-300 transition-colors uppercase tracking-widest text-xs"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                      aria-label={`Explore our ${featured.title} services`}
                    >
                      {t('explore', 'Explore')} {featured.title} <ArrowUpRight size={16} aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </motion.div>
            )}

            {/* Smaller Cards */}
            {rest.map((service, idx) => (
              <motion.div 
                key={service.id}
                className="group bg-stone-100 p-8 min-h-[200px] flex flex-col justify-between hover:bg-stone-200 transition-colors duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx + 1) * 0.1 }}
              >
                <div className="flex justify-between items-start mb-6">
                  <span 
                    className="text-sm uppercase tracking-widest text-stone-700"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {String(idx + 2).padStart(2, '0')}
                  </span>
                  <div className="text-stone-600 group-hover:text-stone-900 transition-colors">
                    {iconMap[service.id] || <Sparkles size={20} strokeWidth={1} />}
                  </div>
                </div>
                <div>
                  <h3 
                    className="text-xl mb-3 text-stone-900"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {service.title}
                  </h3>
                  <p 
                    className="text-stone-700 text-sm font-light"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // VISTA LAYOUT - Full-width dramatic cards
  if (layout === 'vista') {
    return (
      <section className="py-24 md:py-40 bg-stone-900 text-stone-50" id="services">
        <div className="container mx-auto px-6 md:px-12">
          <div className="mb-20">
            <h2 
              className="text-5xl md:text-7xl italic text-center"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title || t('howWeServe', 'How We Serve')}
            </h2>
          </div>

          <div className="flex flex-col gap-12">
            {services.map((service, idx) => (
              <motion.div 
                key={service.id}
                className="group flex flex-col md:flex-row items-start md:items-center gap-8 py-12 border-t border-stone-800"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <span 
                  className="text-7xl md:text-9xl text-stone-800 group-hover:text-stone-600 transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300 }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="flex-1">
                  <h3 
                    className="text-3xl md:text-4xl mb-4 group-hover:italic transition-all duration-500"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {service.title}
                  </h3>
                  <p 
                    className="text-stone-400 font-light max-w-xl"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {service.description}
                  </p>
                </div>
                {service.link && (
                  <Link 
                    href={service.link}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ArrowUpRight size={24} strokeWidth={1} />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // NUMBERED LIST (LEDGER) LAYOUT - Original editorial style
  return (
    <section className="py-24 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-px bg-stone-900" />
            <span 
              className="text-xs tracking-[0.3em] uppercase text-stone-500"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('service', 'Services')}
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <h2 
              className="text-4xl lg:text-5xl xl:text-6xl text-stone-900 tracking-tight"
              style={{ 
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontWeight: 300,
              }}
            >
              {title}
            </h2>
            
            {subtitle && (
              <div className="lg:pt-4">
                <p 
                  className="text-stone-500 text-base lg:text-lg max-w-md"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {subtitle}
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Services List */}
        <div className="space-y-0">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group border-t border-stone-200 py-8 lg:py-10 hover:bg-stone-100 transition-colors duration-300 -mx-6 px-6 lg:-mx-12 lg:px-12"
            >
              <div className="grid grid-cols-12 gap-4 lg:gap-8 items-start">
                {/* Number */}
                <div className="col-span-2 lg:col-span-1">
                  <span 
                    className="text-2xl lg:text-3xl text-stone-300 group-hover:text-stone-900 transition-colors duration-300"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontWeight: 300,
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                
                {/* Title */}
                <div className="col-span-10 lg:col-span-4">
                  <h3 
                    className="text-xl lg:text-2xl text-stone-900 group-hover:italic transition-all duration-300"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontWeight: 400,
                    }}
                  >
                    {service.title}
                  </h3>
                </div>
                
                {/* Description */}
                <div className="col-span-12 lg:col-span-5 lg:col-start-6">
                  <p 
                    className="text-stone-500 text-sm lg:text-base leading-relaxed"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {service.description}
                  </p>
                </div>
                
                {/* Arrow */}
                <div className="hidden lg:flex col-span-2 justify-end">
                  {service.link && (
                    <Link
                      href={service.link}
                      className="w-10 h-10 border border-stone-200 group-hover:border-stone-900 flex items-center justify-center transition-all duration-300 group-hover:bg-stone-900 group-hover:text-stone-50"
                    >
                      <ArrowUpRight size={16} strokeWidth={1.5} />
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          {/* Final border */}
          <div className="border-t border-stone-200" />
        </div>
      </div>
    </section>
  );
}
